/**
 * home/sections/HeaderSection.tsx
 * Cabecera: logo + navegación + theme toggle
 *
 * @version 2.9.0
 * @changes
 *   2.0.0 — H.02: clases DS v2 + logo flip 3D
 *   2.1.0 — nav adapta comportamiento según ruta activa (useLocation)
 *   2.2.0 — FIX: menú hamburger en móvil
 *   2.3.0 — UX: botón único «Acceder» (login + registro en un modal)
 *   2.4.0 — FB.10.12: user-chip → popover perfil (ESC/click-fuera)
 *   2.5.0 — T-12: inline style scaleX(-1) → clase CSS .logo-icon__map
 *   2.6.0 — BRAND-01: animación Draw (stroke-dashoffset); logoPlaying state
 *   2.7.0 — B2-T05: fix StrictMode (split mount/nav effects; setTimeout 80ms)
 *   2.8.0 — T-12: CSS v2.1.0 aplicado · flip horizontal opción 4 activo
 *   2.9.0 — FIX: animación mount solo se lanza si la ruta inicial es «/»
 */
import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { IconMap, IconSun, IconMoon } from '../icons';
import './HeaderSection.css';

// ============================================
// TYPES
// ============================================

type Theme = 'dark' | 'light';

export interface HeaderSectionProps {
  /** Tema activo */
  theme: Theme;
  /** Alterna entre claro y oscuro */
  onToggleTheme: () => void;
  /** Usuario está autenticado */
  isAuthenticated: boolean;
  /** Nombre de usuario autenticado (nombre completo o email-prefix) */
  username?: string;
  /** Abre el modal de autenticación. Modo opcional: 'login' (por defecto) | 'register' */
  onLoginClick: (mode?: 'login' | 'register') => void;
  /** Cierra sesión del usuario */
  onLogout: () => void | Promise<void>;
  /** El usuario autenticado es administrador */
  isAdmin?: boolean;
  /** Abre el panel de administración */
  onOpenAdmin?: () => void;
  /** El usuario autenticado es moderador (SEC0-b2c-2) */
  isModerator?: boolean;
  /** Abre el panel de moderación (SEC0-b2c-2) */
  onOpenModerator?: () => void;
  /** Abre el formulario de solicitud de editor */
  onRequestEditor?: () => void;
  /** Abre el modal del conversor de coordenadas */
  onOpenConversor?: () => void;
}

// ============================================
// COMPONENT
// ============================================

export const HeaderSection: FC<HeaderSectionProps> = ({
  theme, onToggleTheme, isAuthenticated, username, onLoginClick, onLogout,
  isAdmin = false, onOpenAdmin, isModerator = false, onOpenModerator,
  onRequestEditor, onOpenConversor,
}) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const isHome    = location.pathname === '/';
  const isRoadmap = location.pathname === '/roadmap';
  const isFaq     = location.pathname === '/faq';

  const [menuOpen,    setMenuOpen]    = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { canRequestEditor } = usePermissions();
  const popoverRef = useRef<HTMLDivElement>(null);

  // Cierra el menú al cambiar de ruta
  useEffect(() => {
    if (!menuOpen) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- cierre de menú al cambiar ruta; guarda explícita
    setMenuOpen(false);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cierra el popover con ESC o click fuera
  useEffect(() => {
    if (!popoverOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopoverOpen(false);
    };
    const onOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('keydown',   onKey);
    document.addEventListener('mousedown', onOutside);
    return () => {
      document.removeEventListener('keydown',   onKey);
      document.removeEventListener('mousedown', onOutside);
    };
  }, [popoverOpen]);

  const [logoPlaying, setLogoPlaying] = useState(false);

  const prevPathRef = useRef<string>('');

  // Animación en mount: solo si la carga inicial es la Home
  // (evita que se lance al entrar directamente a /roadmap, /faq, etc.)
  useEffect(() => {
    if (!isHome) return;
    const timer = setTimeout(() => {
       
      setLogoPlaying(true);
    }, 80);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-animación al navegar de vuelta a home
  useEffect(() => {
    const prev = prevPathRef.current;
    const curr = location.pathname;
    prevPathRef.current = curr;

    if (!prev || curr !== '/' || prev === '/') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset a false antes del rAF; necesario para relanzar animación CSS
    setLogoPlaying(false);
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setLogoPlaying(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  const goHome = (sectionId?: string) => {
    setMenuOpen(false);
    if (isHome) {
      if (sectionId) {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      if (sectionId) sessionStorage.setItem('scrollTo', sectionId);
      navigate('/');
    }
  };

  return (
    <header className="header">
      <div className="header__inner">
        {/* ── Logo ── */}
        <a
          href="#/"
          className={`logo${logoPlaying ? ' logo--playing' : ''}`}
          style={{ position: 'relative' }}
          onMouseEnter={() => {
            setLogoPlaying(false);
            requestAnimationFrame(() => requestAnimationFrame(() => setLogoPlaying(true)));
          }}
          onClick={(e) => {
            e.preventDefault();
            if (isHome) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate('/');
            }
          }}
        >
          <div className="logo-icon" style={{ width: 40, height: 40 }}>
            <span className="logo-icon__map">
              <IconMap />
            </span>
          </div>
          <span className="logo-text">
            <span className="logo-ptel-part">PTEL</span>
            {' '}
            <span className="logo-tool-part">Tool</span>
          </span>
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: -6,
              right: -60,
              fontSize: 9,
              opacity: 0.3,
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '0.04em',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            v{__APP_VERSION__}
          </span>
        </a>

        {/* ── Nav (escritorio) ── */}
        <nav className={`nav${menuOpen ? ' nav--open' : ''}`}>
          <a
            href="#/"
            className={isHome ? 'active' : ''}
            onClick={(e) => { e.preventDefault(); goHome(); }}
          >
            Inicio
          </a>
          <a
            href="#features"
            onClick={(e) => { e.preventDefault(); goHome('features'); }}
          >
            Funciones
          </a>
          <a
            href="#sources"
            onClick={(e) => { e.preventDefault(); goHome('sources'); }}
          >
            APIs
          </a>
          {onOpenConversor && (
            <a
              href="#conversor"
              onClick={(e) => { e.preventDefault(); setMenuOpen(false); onOpenConversor(); }}
            >
              Conversor UTM
            </a>
          )}
          <a
            href="#/roadmap"
            className={isRoadmap ? 'active' : ''}
            onClick={(e) => { e.preventDefault(); navigate('/roadmap'); setMenuOpen(false); }}
          >
            Hoja de ruta
          </a>
          <a
            href="#/faq"
            className={isFaq ? 'active' : ''}
            onClick={(e) => { e.preventDefault(); navigate('/faq'); setMenuOpen(false); }}
          >
            FAQ
          </a>
        </nav>

        {/* ── Zona derecha: auth + hamburger + theme ── */}
        <div className="header-auth">
          {isAuthenticated ? (
            <>
              {/* Botón Admin — solo para administradores */}
              {isAdmin && onOpenAdmin && (
                <button
                  className="header-auth__admin-btn"
                  onClick={onOpenAdmin}
                  aria-label="Abrir panel de administración"
                  title="Gestión de usuarios"
                  type="button"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                    background: 'transparent', border: '1px solid var(--teal, #2dd4bf)',
                    borderRadius: 6, color: 'var(--teal, #2dd4bf)', cursor: 'pointer',
                    fontSize: 13, fontWeight: 600,
                  }}
                >
                  Admin
                </button>
              )}
              {/* Botón Moderación — solo para moderadores (SEC0-b2c-2) */}
              {isModerator && onOpenModerator && (
                <button
                  className="header-auth__admin-btn"
                  onClick={onOpenModerator}
                  aria-label="Abrir panel de moderación"
                  title="Solicitudes de editor"
                  type="button"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                    background: 'transparent', border: '1px solid var(--teal, #2dd4bf)',
                    borderRadius: 6, color: 'var(--teal, #2dd4bf)', cursor: 'pointer',
                    fontSize: 13, fontWeight: 600,
                  }}
                >
                  Moderación
                </button>
              )}
              {/* User chip → abre popover de perfil */}
              <div className="user-chip-wrapper" ref={popoverRef}>
                <div
                  className="user-chip"
                  data-testid="user-chip"
                  style={{ cursor: 'pointer' }}
                  aria-haspopup="true"
                  aria-expanded={popoverOpen}
                  onClick={() => setPopoverOpen(o => !o)}
                >
                  <div className="user-avatar">
                    {(username ?? 'U').slice(0, 2).toUpperCase()}
                  </div>
                  {username ?? 'Usuario'}
                </div>

                {popoverOpen && (
                  <div className="user-popover" role="menu" aria-label="Opciones de usuario">
                    <div className="user-popover__name">
                      {username ?? 'Usuario'}
                    </div>
                    <div className="user-popover__sep" aria-hidden="true" />
                    {canRequestEditor && onRequestEditor && (
                      <>
                        <button
                          className="user-popover__btn"
                          type="button"
                          role="menuitem"
                          onClick={() => { setPopoverOpen(false); onRequestEditor(); }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round"
                              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                          </svg>
                          Solicitar rol Editor
                        </button>
                        <div className="user-popover__sep" aria-hidden="true" />
                      </>
                    )}
                    <button
                      className="user-popover__btn"
                      data-testid="logout-btn"
                      type="button"
                      role="menuitem"
                      onClick={() => { setPopoverOpen(false); void onLogout(); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1"/>
                      </svg>
                      Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* ── Dos botones: «Crear cuenta» (ghost) + «Accede» (teal) ── */
            <>
              <button
                className="btn-crear-cuenta"
                onClick={() => onLoginClick('register')}
                type="button"
                aria-label="Crear una cuenta en PTEL Tool"
              >
                Crear cuenta
              </button>
              <button
                className="btn-acceder"
                onClick={() => onLoginClick('login')}
                type="button"
                aria-label="Acceder a PTEL Tool"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3"/>
                </svg>
                Accede
              </button>
            </>
          )}

          {/* ── Hamburger (solo móvil) ── */}
          <button
            className="header-hamburger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            type="button"
          >
            {menuOpen ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            )}
          </button>

          <button
            className="theme-switch"
            onClick={onToggleTheme}
            aria-label="Cambiar tema"
            type="button"
          >
            <span className="theme-switch__current">
              {theme === 'dark' ? <IconSun /> : <IconMoon />}
            </span>
            <span className="theme-switch__hover">
              {theme === 'dark' ? <IconMoon /> : <IconSun />}
            </span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default HeaderSection;
